/**
 * AddOn catalog API contracts (plan #004, U5).
 *
 * An AddOn is its own catalog entity (KTD-8) that grants features via
 * AddOnFeature links (each referencing a `Feature` by public id — KTD-4).
 * It may be optionally product-scoped via `productId` (null = universal).
 * Feature links are managed as a full set on create/update.
 *
 * Endpoints (all super-admin gated, platform-global):
 *  - GET  /v1/catalog/add-ons        → list add-ons
 *  - POST /v1/catalog/add-ons        → create an add-on (+ feature links)
 *  - PATCH /v1/catalog/add-ons/:id   → update an add-on (+ replace feature links)
 */

import { z } from 'zod'
import { AddOnSchema } from '../../schemas/entities/add-on'

/**
 * A single add-on→feature link in a create/update request. References the
 * granted `Feature` by its public id (KTD-4).
 */
export const AddOnFeatureLinkInputSchema = z.object({
  featureId: z.string().min(1, 'featureId is required'),
  isEnabled: z.boolean().optional(),
  config: z.record(z.string(), z.unknown()).nullable().optional()
})
export type AddOnFeatureLinkInput = z.infer<typeof AddOnFeatureLinkInputSchema>

// ----- Create -----

export const CreateAddOnRequestSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens'),
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  /** Optional owning Product public id; omitted/null = universal add-on. */
  productId: z.string().min(1).nullable().optional(),
  isActive: z.boolean().optional(),
  features: z.array(AddOnFeatureLinkInputSchema).optional()
})
export type CreateAddOnRequest = z.infer<typeof CreateAddOnRequestSchema>

// ----- Update (identity fixed; features, when present, REPLACE the set) -----

export const UpdateAddOnRequestSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  /** Optional owning Product public id; null = universal. Omit to leave unchanged. */
  productId: z.string().min(1).nullable().optional(),
  isActive: z.boolean().optional(),
  /** When provided, fully replaces the add-on's feature links (full-payload round-trip). */
  features: z.array(AddOnFeatureLinkInputSchema).optional()
})
export type UpdateAddOnRequest = z.infer<typeof UpdateAddOnRequestSchema>

// ----- Responses -----

export const AddOnResponseSchema = z.object({
  data: AddOnSchema,
  message: z.string()
})
export type AddOnResponse = z.infer<typeof AddOnResponseSchema>

export const ListAddOnsResponseSchema = z.object({
  data: z.array(AddOnSchema),
  message: z.string()
})
export type ListAddOnsResponse = z.infer<typeof ListAddOnsResponseSchema>
